"use client";

import { Play } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AttorneyHeroMinimal() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: wordRef,
    isVisible: wordVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.15
  });
  const ledger = [{
    label: "1983年以来"
  }, {
    label: "HOLLYWOOD, FL"
  }, {
    label: "ESTATE & BUSINESS"
  }];
  return <section data-section-id="1858" id="attorneyherominimal" className="relative overflow-hidden bg-primary text-primary-foreground py-20 md:py-28 lg:py-32">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-full w-24 md:w-40 opacity-20" viewBox="0 0 160 800" fill="none" preserveAspectRatio="none">
        <path d="M40 0 C 70 120, 20 220, 60 340 S 100 560, 50 700 L 50 800" stroke="currentColor" strokeWidth="1" className="text-primary-foreground/30" />
      </svg>

      {}
      <svg aria-hidden="true" className="pointer-events-none absolute -right-24 top-16 hidden md:block h-[720px] w-[640px] opacity-20" viewBox="0 0 640 720" fill="none">
        <path d="M520 90 C 600 160, 640 280, 600 400 C 560 540, 440 640, 300 660 C 180 680, 60 600, 40 460 C 20 320, 80 180, 200 110 C 320 40, 440 30, 520 90 Z" fill="currentColor" className="text-primary-foreground/10" />
      </svg>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 md:px-12 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className="lg:col-span-8 transition-all duration-[720ms] ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            {}
            <div className="flex items-center gap-3 mb-10">
              <span className="font-mono text-[0.75rem] uppercase tracking-[0.1em] text-secondary font-medium">
                フロリダ州・ニューヨーク州弁護士
              </span>
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-primary-foreground/60">
                · §01 — ESTABLISHED
              </span>
            </div>

            {}
            <h1 className="font-serif font-bold text-primary-foreground leading-[1.1] tracking-tight mb-8" style={{
            fontSize: "clamp(2.75rem, 5.5vw, 5rem)"
          }}>モーリー・I・レヴィーン弁護士<span className="block font-serif italic text-secondary font-normal mt-3 transition-all duration-[600ms] ease-out" data-ast-id="f:/src/pages/MollyLevinAttorney/sections/AttorneyHeroMinimal.tsx|sig:qosrj" data-ast-start="2775" data-ast-end="3153" data-attr-property-mappings="ref:wordRef" style={{
              opacity: '1',
              transform: 'translateY(0px)',
              transitionDelay: '220ms'
            }}>South Florida Legal Counsel</span></h1>

            {}
            <p className="font-default text-primary-foreground/90 max-w-[540px] mb-12 leading-[1.7]" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.2rem)"
          }}>フロリダ州ハリウッドを拠点に、ビジネス、不動産取引、遺言・信託・プロベート、弁護士費用に関する案件を取り扱っています。当事務所では、日本語による初期連絡、情報整理、書類整理にも対応しています。</p>

            {}
            <div className="flex flex-wrap items-center gap-4 mb-14">
              <Button asChild className="h-[52px] rounded-lg px-7 bg-secondary text-secondary-foreground hover:bg-secondary/90 font-medium tracking-[0.01em] transition-all duration-220 hover:-translate-y-[1.5px]">
                <Link to="/InitialContactInJapanesePage">相談を申し込む</Link>
              </Button>
              <Button asChild variant="outline" className="h-[52px] rounded-lg px-7 bg-transparent border border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground font-medium tracking-[0.01em] transition-all duration-220">
                <Link to="/ContactUsPage">
                  <Play className="size-3.5" strokeWidth={1.5} />
                  Contact Us
                </Link>
              </Button>
            </div>

            {}
            <div className="pt-6 border-t border-primary-foreground/20">
              <div className="flex items-center gap-3 mb-3">
                <span className="block h-1.5 w-1.5 rounded-lg bg-secondary" />
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-primary-foreground/60">
                  Practice Areas — Comprehensive
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {ledger.map((item, i) => <span data-index={i} key={item.label} className="font-mono text-xs sm:text-sm tracking-[0.06em] text-primary-foreground/85">
                    {item.label}
                    {i < ledger.length - 1 && <span className="ml-6 text-primary-foreground/30">·</span>}
                  </span>)}
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-4 relative transition-all duration-[720ms] ease-out" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transitionDelay: "120ms"
        }}>
            {}
            <svg aria-hidden="true" className="pointer-events-none absolute -inset-8 -z-0" viewBox="0 0 400 500" fill="none" preserveAspectRatio="none">
              <path d="M340 60 C 400 130, 410 260, 360 360 C 310 460, 180 480, 90 420 C 0 360, -10 220, 50 130 C 110 40, 280 -10, 340 60 Z" fill="currentColor" className="text-primary-foreground/5" />
            </svg>

            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg shadow-xl border border-primary-foreground/10">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681339/img/hollywood_office_exterior.png" alt="Professional exterior view of the law office in Hollywood, Florida" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>

            {}
            <div className="mt-5 flex items-center gap-3 px-1">
              <span className="block h-px w-8 bg-secondary" />
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-primary-foreground/70">
                Hollywood, FL · Established 1983
              </span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-primary-foreground/20" />
    </section>;
}